// Per spec/blocks.md §host-committee (P-037.28 / D-136). Fork of
// `member-list` for the event host-committee email pattern. Each
// member renders as a 2-line text card inside the system 3-col grid
// (D-117 30/190/25/190/25/190/30 rhythm).
//
// **Render per member:**
//   {committeeRoleTitle}    (14 px bold; humanizes cleanly to
//                            "Committee Role Title" via the form
//                            generator)
//   {memberAndPartner}      (14 px regular; the field label
//                            overrides via .describe('Member +
//                            Spouse/Partner') — see schema-to-fields.ts;
//                            non-brand describe strings flow through as
//                            the form-side label)
//
// **No baked SubSectionHeader** — D-126 convention. Staff composes
// `section-heading` / `sub-section-heading` above when wanted.
//
// **Form-label override mechanism for `memberAndPartner`.** The form
// generator's schema-to-fields walker treats `.describe(...)` as a
// label override unless the string matches one of the registered
// brand strings (`richText`, `imageUrl`, `derived`). "Member +
// Spouse/Partner" is none of those, so the walker uses it as the
// rendered label. No new mechanism needed; pure mechanics through
// the existing label dispatch.
//
// **No cap on members array** — chapter-scale lists never exceed ~15
// committee members.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'
import { tokens } from '../tokens'
import { text } from '../primitives/text'
import { gridLayout } from '../components/helpers'
import { HtmlString } from '../types'

const memberSchema = z.object({
  committeeRoleTitle: z.string().min(1),
  memberAndPartner: z.string().min(1).describe('Member + Spouse/Partner'),
})

type CommitteeMember = z.infer<typeof memberSchema>

const optionsSchema = z.object({})

const inputsSchema = z.object({
  members: z.array(memberSchema),
})

const TIGHT_TOP_PAD = tokens.space[1]

function memberCard(m: CommitteeMember): HtmlString {
  const titleHtml = text({
    text: m.committeeRoleTitle,
    size: tokens.type.size.sm,
    weight: tokens.type.weight.bold,
  })
  const memberHtml = text({ text: m.memberAndPartner, size: tokens.type.size.sm })
  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td>${titleHtml}</td></tr><tr><td style="padding-top: ${TIGHT_TOP_PAD}px;">${memberHtml}</td></tr></table>`
}

export const hostCommittee = defineBlock({
  id: 'host-committee',
  label: 'Host Committee',
  category: 'content',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: { members: [] },
  autoHide: ({ inputs }) => inputs.members.length === 0,
  render: ({ inputs }) => {
    // P-037.29 post-Phase-Close §item D — per-row segmented dividers
    // BETWEEN successive card rows (no divider above the first row).
    // Same convention as member-list / day-chairs (D-118).
    const grid = gridLayout(inputs.members, 3, (m) => memberCard(m), {
      rowDivider: { color: tokens.color['rule-subtle'], padY: 20 },
    })
    // 30 px L/R padded outer cell — matches event-card-group /
    // chapter-sponsors / prospect-list outer rhythm. Top/bottom
    // padding lives on the system-level inter-Block spacer
    // (`src/render/block-padding.ts`) per P-037.29 D-137 §item 2.
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="padding: 0 30px;">${grid}</td></tr></table>`
  },
})
